const INLINE_EVENT_ATTRS = [
	"onclick",
	"onmouseover",
	"onmouseout",
	"onmouseenter",
	"onmouseleave",
	"onmousemove",
	"ondblclick",
	"onmousedown",
	"onmouseup",
	"onwheel",
	"onfocus",
	"onblur",
] as const

const INLINE_EVENT_SELECTOR = INLINE_EVENT_ATTRS.map((attr) => `[${attr}]`).join(",")

const HOTSPOT_PREFIX = "hotspot."

export const pipe =
	<T>(...fns: Array<(value: T) => T>) =>
	(value: T): T =>
		fns.reduce((acc, fn) => fn(acc), value)

export const fetchSVGText = async (url: string): Promise<string> => {
	const res = await fetch(url)
	if (!res.ok) throw new Error(`Failed to fetch SVG (${res.status})`)
	return res.text()
}

export const parseSVG = (svgText: string): SVGSVGElement | null => {
	const doc = new DOMParser().parseFromString(svgText, "image/svg+xml")
	if (doc.querySelector("parsererror")) return null
	return doc.querySelector("svg")
}

const removeScripts = (svg: SVGSVGElement): SVGSVGElement => {
	svg.querySelectorAll("script").forEach((node) => node.remove())
	return svg
}

const removeInlineEvents = (svg: SVGSVGElement): SVGSVGElement => {
	svg.querySelectorAll(INLINE_EVENT_SELECTOR).forEach((node) => {
		const onmousemove = node.getAttribute(INLINE_EVENT_ATTRS[5])
		const match = onmousemove?.match(/ShowToolTip\(evt,'[^']*',"([^"]+)"\)/)
		if (match) node.setAttribute("data-part-number", match[1])

		INLINE_EVENT_ATTRS.forEach((attr) => node.removeAttribute(attr))
	})
	return svg
}

export const sanitizeSVG = pipe<SVGSVGElement>(removeScripts, removeInlineEvents)

export const makeResponsive = (svg: SVGSVGElement): SVGSVGElement => {
	svg.removeAttribute("width")
	svg.removeAttribute("height")
	svg.setAttribute("width", "100%")
	svg.setAttribute("height", "100%")
	svg.setAttribute("preserveAspectRatio", "xMidYMid meet")
	svg.style.display = "block"
	return svg
}

export const prepareSVG = pipe<SVGSVGElement>(sanitizeSVG, makeResponsive)

export const mountInto = (container: HTMLElement, svg: SVGSVGElement): void => {
	container.innerHTML = ""
	container.appendChild(svg)
}

export const queryHotspots = (container: HTMLElement): HTMLElement[] =>
	Array.from(container.querySelectorAll<HTMLElement>(`[id^='${HOTSPOT_PREFIX}']`))

export const findHotspotById = (container: HTMLElement, id: string): HTMLElement | null =>
	container.querySelector<HTMLElement>(`[id='${HOTSPOT_PREFIX}${id}']`)

export const findHotspotByPartNumber = (
	container: HTMLElement,
	partNumber: string
): HTMLElement | null => {
	const exact = container.querySelector<HTMLElement>(`[data-part-number='${partNumber}']`)
	if (exact) return exact

	// Fallback: match by base (e.g. "48-615-05" matches "48-615-05-PP" when NCS has "48-615-05-CL")
	const base = partNumber.split("-").slice(0, -1).join("-")
	if (!base) return null
	return container.querySelector<HTMLElement>(`[data-part-number^='${base}-']`)
}

export const extractHotspotId = (fullId: string): string => fullId.replace(HOTSPOT_PREFIX, "")
